import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useProviderAuth } from '@/contexts/ProviderAuthContext';
import { toast } from 'sonner';

export default function ProviderLoginPage() {
  const navigate = useNavigate();
  const { login, register } = useProviderAuth();
  const [mode, setMode] = useState('login');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    clinic_name: '',
    specialty: '',
    license_id: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(formData.email, formData.password);
        toast.success('Welcome back, provider');
      } else {
        await register({
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name,
          clinic_name: formData.clinic_name,
          specialty: formData.specialty,
          license_id: formData.license_id
        });
        toast.success('Provider account created');
      }
      navigate('/provider/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="provider-login-page">
      <div className="w-full max-w-lg">
        <Link to="/" className="text-sm text-slate-500 hover:text-slate-700">← Back to LYFSYNC</Link>
        <Card className="mt-6 border-slate-200 shadow-lg">
          <CardHeader>
            <div className="flex items-center gap-3 mb-3">
              <img src="/lyfsync-logo.png" alt="LYFSYNC logo" className="w-10 h-10 object-contain" data-testid="provider-login-logo" />
              <div>
                <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Provider Access
                </CardTitle>
                <CardDescription>Secure access for registered medical providers</CardDescription>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={mode === 'login' ? 'default' : 'outline'}
                onClick={() => setMode('login')}
                className="flex-1"
                data-testid="provider-login-tab"
              >
                Sign In
              </Button>
              <Button
                type="button"
                variant={mode === 'register' ? 'default' : 'outline'}
                onClick={() => setMode('register')}
                className="flex-1"
                data-testid="provider-register-tab"
              >
                Register
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="providerEmail">Email</Label>
                <Input
                  id="providerEmail"
                  type="email"
                  value={formData.email}
                  onChange={handleChange('email')}
                  required
                  data-testid="provider-email-input"
                />
              </div>

              {mode === 'register' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="providerName">Full Name</Label>
                    <Input
                      id="providerName"
                      value={formData.full_name}
                      onChange={handleChange('full_name')}
                      required
                      data-testid="provider-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="providerClinic">Clinic / Hospital</Label>
                    <Input
                      id="providerClinic"
                      value={formData.clinic_name}
                      onChange={handleChange('clinic_name')}
                      required
                      data-testid="provider-clinic-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="providerSpecialty">Specialty</Label>
                    <Input
                      id="providerSpecialty"
                      value={formData.specialty}
                      onChange={handleChange('specialty')}
                      required
                      data-testid="provider-specialty-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="providerLicense">Medical License ID</Label>
                    <Input
                      id="providerLicense"
                      value={formData.license_id}
                      onChange={handleChange('license_id')}
                      required
                      data-testid="provider-license-input"
                    />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="providerPassword">Password</Label>
                <Input
                  id="providerPassword"
                  type="password"
                  value={formData.password}
                  onChange={handleChange('password')}
                  required
                  data-testid="provider-password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-blue-900 hover:bg-blue-800 text-white"
                disabled={loading}
                data-testid="provider-submit-btn"
              >
                {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}